import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, cx } from '@/components/ui';

/**
 * 전역 확인창.
 *
 * 🔴 window.confirm 을 쓰지 않는다 (2026-08-16 결정)
 *   브라우저 기본 확인창은 우리가 손댈 수 없다. 다크 모드를 따르지 않고,
 *   폰에서는 주소창 아래 붙어 어색하며, 버튼 문구를 "확인/취소" 외로 바꿀 수 없다.
 *   "정말 삭제하시겠습니까?" 옆에 [확인] 만 있으면 무엇이 확인인지 한 번 더 생각해야 한다.
 *
 * 왜 컴포넌트가 아니라 훅인가
 *   확인창은 "이 값을 받아서 다음으로 넘어간다" 는 흐름이다.
 *   컴포넌트로 두면 열림 상태와 대상 id 를 화면마다 useState 로 들고 있어야 하고,
 *   그 상태가 화면 하나에 두세 개씩 늘어난다.
 *
 *   훅으로 두면 부르는 쪽이 await 한 줄이면 된다.
 *
 *     const confirm = useConfirm();
 *     if (!await confirm({ title: '세션을 종료할까요?' })) return;
 *     terminate();
 */

interface ConfirmOptions {
  title: string;
  /** 이 동작이 무엇을 하는지. 사용자가 결과를 예측할 수 있어야 한다 */
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** 되돌릴 수 없는 동작이면 danger. 확인 버튼이 붉어진다 */
  tone?: 'default' | 'danger';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * 확인창을 띄우고 사용자의 선택을 기다린다.
 *
 * @returns 확인이면 true, 취소하거나 배경을 누르거나 ESC 면 false
 */
export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);

  if (!confirm) {
    // Provider 밖에서 부르면 조용히 아무 일도 안 일어나는 것이 가장 나쁘다.
    // 확인 없이 삭제가 그대로 진행되는 것보다 여기서 터지는 편이 낫다.
    throw new Error('useConfirm 은 ConfirmProvider 안에서만 쓸 수 있다.');
  }
  return confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);

  /*
    resolve 를 ref 에 둔다.

    state 에 넣으면 확인창이 열려 있는 동안 리렌더가 일어날 때마다
    함수 참조가 바뀌어, 어느 시점의 resolve 를 부르는지 추적하기 어려워진다.
    ref 는 렌더와 무관하게 같은 자리를 가리킨다.
  */
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    setOptions(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && <ConfirmDialog options={options} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({
  options,
  onClose,
}: {
  options: ConfirmOptions;
  onClose: (result: boolean) => void;
}) {
  useEffect(() => {
    /*
      ESC 로 닫는다. 확인창에서 가장 자주 쓰이는 탈출구다.

      capture 단계에서 잡는 이유
        모달 뒤의 화면에도 ESC 핸들러가 있을 수 있다 (종목 상세 모달 등).
        확인창이 떠 있는 동안에는 그쪽이 먼저 반응하면 안 된다.
    */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);

    /*
      뒤 화면 스크롤을 막는다.
      폰에서 확인창 위를 밀면 뒤 목록이 움직여 어디를 보고 있었는지 잃는다.
    */
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const danger = options.tone === 'danger';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      // 배경을 누르면 취소. 확인이 아니라 취소인 것은 되돌릴 수 없는 쪽을
      // 실수로 고르지 않게 하기 위해서다.
      onClick={() => onClose(false)}
      role="presentation"
    >
      {/*
        폰에서는 아래에서 올라오는 시트, 데스크탑에서는 가운데 카드.
        폰에서 화면 가운데 띄우면 엄지가 닿지 않는 자리에 버튼이 생긴다.
      */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={options.title}
        onClick={(event) => event.stopPropagation()}
        className={cx(
          'w-full max-w-sm bg-surface p-5 shadow-xl',
          'rounded-t-2xl pb-[max(1.25rem,env(safe-area-inset-bottom))]',
          'sm:rounded-2xl sm:pb-5',
        )}
      >
        <h2 className="text-base font-semibold text-fg">{options.title}</h2>

        {options.description && (
          <div className="mt-2 text-sm leading-relaxed text-fg-muted">{options.description}</div>
        )}

        {/*
          취소가 왼쪽, 확인이 오른쪽이다.
          오른쪽 엄지가 닿는 자리에 "진행" 을 두는 것이 국내 앱의 관행이고,
          되돌릴 수 없는 동작은 색으로 한 번 더 구분한다.
        */}
        <div className="mt-5 flex gap-2">
          <Button variant="secondary" fullWidth onClick={() => onClose(false)}>
            {options.cancelText ?? '취소'}
          </Button>
          {/*
            autoFocus 로 확인 버튼에 초점을 준다. 키보드만으로 Enter 로 끝낼 수 있다.
            ref 를 쓰지 않는 이유는 공유 Button 이 ref 를 전달하지 않기 때문이다 —
            확인창 하나 때문에 그것을 forwardRef 로 바꾸면 모든 버튼이 영향을 받는다.
          */}
          <Button
            autoFocus
            variant={danger ? 'danger' : 'primary'}
            fullWidth
            onClick={() => onClose(true)}
          >
            {options.confirmText ?? '확인'}
          </Button>
        </div>
      </div>
    </div>
  );
}
